import React from 'react';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { Star, Edit, MoreHorizontal, Check, Upload, RotateCcw, Download } from 'lucide-react';
import { isDarkAtom } from '../../../atoms/shared/uiAtoms';
import { Character } from '../../../types/character';
import {
  publishToMarketAtom,
  withdrawFromMarketAtom,
  unpublishCharacterAtom,
  isCharacterAvailableForConversation
} from '../../../atoms/v3/characterAtoms';

interface CharacterCardProps {
  character: Character;
  isSelected: boolean;
  showActions: boolean; // 是否显示编辑等操作按钮（仅对自己创建的角色卡）
  onCharacterClick: (character: Character) => void;
  onQuickUse: (characterId: string) => void;
  onEdit?: (characterId: string) => void;
}

/**
 * 角色卡组件
 */
export const CharacterCard: React.FC<CharacterCardProps> = ({
  character,
  isSelected,
  showActions,
  onCharacterClick,
  onQuickUse,
  onEdit,
}) => {
  const { t } = useTranslation(['v3/character', 'common']);
  const [isDark] = useAtom(isDarkAtom);

  // 操作原子
  const [, publishToMarket] = useAtom(publishToMarketAtom);
  const [, withdrawFromMarket] = useAtom(withdrawFromMarketAtom);
  const [, unpublishCharacter] = useAtom(unpublishCharacterAtom);

  // 处理卡片点击
  const handleCardClick = (e: React.MouseEvent) => {
    // 如果点击的是操作按钮，不触发卡片点击
    if ((e.target as HTMLElement).closest('.action-button')) {
      return;
    }
    onCharacterClick(character);
  };

  // 处理快速使用
  const handleQuickUse = (e: React.MouseEvent) => {
    e.stopPropagation();
    onQuickUse(character.id);
  };

  // 处理发布到市场
  const handlePublishToMarket = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await publishToMarket({
        id: character.id,
        allowPromptVisible: false, // 默认不允许查看提示词
      });
    } catch (error) {
      console.error('Failed to publish to market:', error);
    }
  };

  // 处理撤回审核
  const handleWithdrawFromMarket = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await withdrawFromMarket(character.id);
    } catch (error) {
      console.error('Failed to withdraw from market:', error);
    }
  };

  // 处理下架
  const handleUnpublish = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await unpublishCharacter(character.id);
    } catch (error) {
      console.error('Failed to unpublish character:', error);
    }
  };

  // 处理编辑
  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onEdit) {
      onEdit(character.id);
    }
  };

  // 获取状态显示
  const getStatusDisplay = () => {
    if (character.type === 'official') return null;
    
    switch (character.status) {
      case 'pending':
        return (
          <span className={`px-2 py-1 text-xs rounded-full ${isDark ? 'bg-ios-dark-orange text-white' : 'bg-orange-100 text-orange-800'}`}>
            {t('v3/character:statusPending', '审核中')}
          </span>
        );
      case 'rejected':
        return (
          <span className={`px-2 py-1 text-xs rounded-full ${isDark ? 'bg-ios-dark-red text-white' : 'bg-red-100 text-red-800'}`}>
            {t('v3/character:statusRejected', '已拒绝')}
          </span>
        );
      default:
        return null;
    }
  };

  // 获取类型标签
  const getTypeLabel = () => {
    switch (character.type) {
      case 'official':
        return (
          <span className={`px-2 py-1 text-xs rounded-full ${isDark ? 'bg-ios-dark-blue text-white' : 'bg-blue-100 text-blue-800'}`}>
            {t('v3/character:typeOfficial', '官方')}
          </span>
        );
      case 'public':
        return (
          <span className={`px-2 py-1 text-xs rounded-full ${isDark ? 'bg-ios-dark-green text-white' : 'bg-green-100 text-green-800'}`}>
            {t('v3/character:typePublic', '公开角色卡')}
          </span>
        );
      case 'private':
        return (
          <span className={`px-2 py-1 text-xs rounded-full ${isDark ? 'bg-ios-dark-gray4 text-ios-dark-label' : 'bg-gray-100 text-gray-800'}`}>
            {t('v3/character:typePrivate', '私密角色卡')}
          </span>
        );
      default:
        return null;
    }
  };

  // 获取提示词可见性标签（私有角色卡不显示）
  const getPromptVisibilityLabel = () => {
    if (character.type === 'private') {
      return null;
    }

    // 官方角色卡默认公开提示词
    if (character.type === 'official') {
      return (
        <span className={`px-2 py-1 text-xs rounded-full ${isDark ? 'bg-ios-dark-green text-white' : 'bg-green-100 text-green-800'}`}>
          {t('v3/character:promptVisible', '公开提示词')}
        </span>
      );
    }

    // 公开角色卡根据设置显示
    if (character.type === 'public') {
      if (character.allowPromptVisible) {
        return (
          <span className={`px-2 py-1 text-xs rounded-full ${isDark ? 'bg-ios-dark-green text-white' : 'bg-green-100 text-green-800'}`}>
            {t('v3/character:promptVisible', '公开提示词')}
          </span>
        );
      } else {
        return (
          <span className={`px-2 py-1 text-xs rounded-full ${isDark ? 'bg-ios-dark-gray4 text-ios-dark-label' : 'bg-gray-100 text-gray-600'}`}>
            {t('v3/character:promptHidden', '隐藏提示词')}
          </span>
        );
      }
    }

    return null;
  };

  return (
    <div
      onClick={handleCardClick}
      className={`
        relative p-4 rounded-xl border cursor-pointer transition-all duration-200
        ${isSelected 
          ? isDark 
            ? 'bg-ios-dark-blue border-ios-dark-blue shadow-lg' 
            : 'bg-blue-50 border-blue-200 shadow-lg'
          : isDark 
            ? 'bg-ios-dark-gray6 border-ios-dark-separator hover:bg-ios-dark-gray5' 
            : 'bg-white border-ios-light-separator hover:bg-gray-50'
        }
        ${isDark ? 'shadow-ios-dark' : 'shadow-ios-light'}
      `}
    >
      {/* 选中状态指示器 */}
      {isSelected && (
        <div className="absolute top-2 left-2">
          <div className={`w-6 h-6 rounded-full flex items-center justify-center ${isDark ? 'bg-white text-ios-dark-blue' : 'bg-blue-500 text-white'}`}>
            <Check size={14} />
          </div>
        </div>
      )}

      {/* 头像区域 */}
      <div className="flex items-start space-x-3 mb-3">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-semibold ${isDark ? 'bg-ios-dark-gray4 text-ios-dark-label' : 'bg-gray-200 text-gray-700'}`}>
          {character.avatarUrl ? (
            <img 
              src={character.avatarUrl} 
              alt={character.name}
              className="w-full h-full rounded-full object-cover"
            />
          ) : (
            character.name.charAt(0).toUpperCase()
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <h3 className={`font-semibold truncate ${isSelected ? (isDark ? 'text-white' : 'text-blue-900') : (isDark ? 'text-ios-dark-label' : 'text-ios-light-label')}`}>
              {character.name}
            </h3>
            {showActions && onEdit && (
              <button
                onClick={handleEdit}
                className={`action-button p-1 rounded-full ${isDark ? 'hover:bg-ios-dark-gray4' : 'hover:bg-gray-200'} transition-colors`}
              >
                <Edit size={14} />
              </button>
            )}
          </div>
          
          <div className="flex items-center space-x-2 mt-1 flex-wrap">
            {getTypeLabel()}
            {getStatusDisplay()}
            {getPromptVisibilityLabel()}
          </div>
        </div>
      </div>

      {/* 描述 */}
      <p className={`text-sm mb-3 line-clamp-2 ${isSelected ? (isDark ? 'text-blue-100' : 'text-blue-800') : (isDark ? 'text-ios-dark-secondary-label' : 'text-ios-light-secondary-label')}`}>
        {character.description}
      </p>

      {/* 创建者和人气信息 */}
      <div className={`flex items-center justify-between text-xs ${isSelected ? (isDark ? 'text-blue-200' : 'text-blue-700') : (isDark ? 'text-ios-dark-tertiary-label' : 'text-ios-light-tertiary-label')}`}>
        <span>
          {character.type === 'official' 
            ? t('v3/character:officialCreator', '官方')
            : character.creatorName || t('v3/character:unknownCreator', '未知用户')
          }
        </span>
        <div className="flex items-center space-x-1">
          <Star size={12} />
          <span>{character.popularityScore}</span>
        </div>
      </div>

      {/* 操作按钮区域 */}
      {showActions && (
        <div className="mt-3 space-y-2">
          {/* 根据角色卡类型和状态显示不同的操作按钮 */}
          {character.type === 'private' && (
            <button
              onClick={handlePublishToMarket}
              className={`action-button w-full py-2 px-4 rounded-lg text-sm font-medium transition-colors flex items-center justify-center space-x-2 ${isDark ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-green-500 text-white hover:bg-green-600'}`}
            >
              <Upload size={14} />
              <span>{t('v3/character:publishToMarket', '发布为公开')}</span>
            </button>
          )}

          {character.type === 'public' && character.status === 'pending' && (
            <button
              onClick={handleWithdrawFromMarket}
              className={`action-button w-full py-2 px-4 rounded-lg text-sm font-medium transition-colors flex items-center justify-center space-x-2 ${isDark ? 'bg-orange-600 text-white hover:bg-orange-700' : 'bg-orange-500 text-white hover:bg-orange-600'}`}
            >
              <RotateCcw size={14} />
              <span>{t('v3/character:withdrawFromMarket', '撤回审核')}</span>
            </button>
          )}

          {character.type === 'public' && character.status === 'approved' && (
            <button
              onClick={handleUnpublish}
              className={`action-button w-full py-2 px-4 rounded-lg text-sm font-medium transition-colors flex items-center justify-center space-x-2 ${isDark ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-red-500 text-white hover:bg-red-600'}`}
            >
              <Download size={14} />
              <span>{t('v3/character:unpublishCharacter', '下架')}</span>
            </button>
          )}
        </div>
      )}

      {/* 快速使用按钮 */}
      <button
        onClick={handleQuickUse}
        className={`
          action-button w-full mt-3 py-2 px-4 rounded-lg text-sm font-medium transition-colors
          ${isSelected
            ? isDark
              ? 'bg-white text-ios-dark-blue hover:bg-gray-100'
              : 'bg-blue-600 text-white hover:bg-blue-700'
            : isDark
              ? 'bg-ios-dark-blue text-white hover:bg-blue-600'
              : 'bg-blue-500 text-white hover:bg-blue-600'
          }
        `}
      >
        {isSelected
          ? t('v3/character:currentlyUsing', '当前使用中')
          : t('v3/character:useCharacter', '使用该角色卡')
        }
      </button>
    </div>
  );
};
